import {
  CurrentUser,
  User,
  WalletModel,
} from '@fantaz/shared/data-access/models';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { AuthIdentity } from './auth.models';
import { AUTH_FEATURE_KEY, AuthState } from './auth.reducer';

// Lookup the 'Auth' feature state managed by NgRx
export const getAuthState = createFeatureSelector<AuthState>(AUTH_FEATURE_KEY);

export const getAuthLoaded = createSelector(
  getAuthState,
  (state: AuthState) => state.loaded,
);

export const getAuthError = createSelector(
  getAuthState,
  (state: AuthState) => state.error,
);
export const getAuthSession = createSelector(
  getAuthState,
  (state: AuthState) => state.session,
);

/**
 * @deprecated
 */
export const getUserId = createSelector(getAuthState, (state: AuthState) => {
  return '';
});

/**
 * @deprecated
 */
export const getUserEmail = createSelector(
  getAuthState,
  (state: AuthState): string => {
    return '';
  },
);

/**
 * @deprecated use isLoggedIn instead
 */
export const getLoggedIn = createSelector(
  getAuthState,
  (state: AuthState): boolean => !!state.isAuthenticated,
);

export const isLoggedIn = createSelector(
  getAuthState,
  (state): boolean => !!state.isAuthenticated,
);

export const getWallet = createSelector(
  getAuthState,
  (state: AuthState): WalletModel | undefined | null => state.wallet || null,
);

export const getCurrentUser = createSelector(
  getAuthState,
  (state: AuthState): CurrentUser | undefined | null =>
    state.currentUser || null,
);
export const selectUser = createSelector(
  getCurrentUser,
  (currentUser) => currentUser?.user,
);

/**
 * Selector for the current user's session
 */
export const getUserSession = createSelector(
  getAuthState,
  (state: AuthState) => state.session,
);

export const getUserLoaded = createSelector(
  getAuthState,
  (state: AuthState) => state.loaded,
);

export const getUserError = createSelector(
  getAuthState,
  (state: AuthState) => state.error,
);

export const getUserLoading = createSelector(
  getAuthState,
  (state: AuthState) => state.loading,
);

export const getAuthUser = createSelector(
  getCurrentUser,
  (state?: CurrentUser | null) => state?.user,
);

export const getMembershipInfo = createSelector(
  getAuthState,
  (state: AuthState) => state.currentUser?.membershipInfo,
);

export const getVerificationStatus = createSelector(
  getAuthUser,
  (user: User | undefined) => user?.verificationStatus,
);

export const getWalletFrozenReason = createSelector(
  getAuthUser,
  (user: User | undefined) => user?.walletFrozenReason,
);

export const getTaxInfoCollectionStatus = createSelector(
  getAuthUser,
  (user: User | undefined) => user?.taxInfoCollectionStatus,
);

export const getUserMemberShipAndTaxCompleted = createSelector(
  getVerificationStatus,
  getTaxInfoCollectionStatus,
  (verificationStatus, taxInfoCollectionStatus): boolean => {
    if (
      taxInfoCollectionStatus &&
      verificationStatus &&
      taxInfoCollectionStatus.approvalDate !== null &&
      verificationStatus.type === 'APPROVED'
    ) {
      return true;
    }

    return false;
  },
);

export const playerProfileComplete = createSelector(
  getAuthUser,
  (user: User | undefined) => {
    if (
      user?.firstName &&
      user?.lastName &&
      user?.legalAddress &&
      user.birthday
    ) {
      return true;
    }

    return false;
  },
);

export const isVerified = createSelector(
  getAuthUser,
  (user: User | undefined) => {
    if (user?.verificationStatus?.type === 'APPROVED') {
      return true;
    }

    return false;
  },
);

export const selectAuthUserContactPoint = createSelector(
  getCurrentUser,
  getAuthSession,
  (
    user,
    session,
  ): {
    // TODO: Move to a type
    email: string | undefined;
    emailVerified?: boolean;
    phone: string | undefined;
    phoneVerified?: boolean;
  } => ({
    email:
      (session?.tokens?.idToken?.payload?.['email'] as string | undefined) ||
      user?.user?.email,
    emailVerified: session?.tokens?.idToken?.payload?.[
      'email_verified'
    ] as boolean,
    phone:
      (session?.tokens?.idToken?.payload?.['phone_number'] as
        | string
        | undefined) || user?.user?.phone,
    phoneVerified: session?.tokens?.idToken?.payload?.[
      'phone_number_verified'
    ] as boolean,
  }),
);
export const selectAuthUserIdentities = createSelector(
  getAuthSession,
  (session) => {
    let identities: AuthIdentity[] = [];
    if (session?.tokens?.idToken?.payload?.['identities']) {
      const it =
        (session.tokens.idToken.payload[
          'identities'
        ] as unknown as AuthIdentity[]) || [];
      identities = it.map((identity) => {
        return {
          userId: identity.userId,
          providerName: identity.providerName,
          providerType: identity.providerType,
          issuer: identity.issuer,
          primary: identity.primary,
          dateCreated: new Date(Number(identity.dateCreated)),
        };
      });
    }
    return identities;
  },
);

export const selectAuthUserSessionPayload = createSelector(
  getAuthState,
  (state) => {
    return state.session?.tokens?.idToken?.payload || null;
  },
);

export const selectAuthUserDevice = createSelector(getAuthState, (state) => {
  return (
    (state.session?.tokens?.accessToken?.payload?.['device_key'] as string) ||
    null
  );
});
